/**
 * <zg-faq-tabs>: category tabs for sections/zg-faq-tabs.liquid.
 *
 * The section renders the tabbed state already (`data-zg-tabbed` on this element, the tab bar with the
 * first tab selected, the other groups `hidden`), so nothing moves when this runs; a <noscript> style in
 * the section shows every group under its heading instead when scripts don't run. This element
 * - gives each group role tabpanel labelled by its tab;
 * - shows one group at a time: click, or ArrowLeft / ArrowRight (wrapping, mirrored in RTL), Home and End
 *   on the focused tab (automatic activation, roving tabindex). Switching category closes the open
 *   question, as the design does;
 * - keeps one question open at a time where the browser ignores <details name> (Firefox < 130,
 *   Safari < 17.2);
 * - opens the category of a link to an element inside it (#faq-<category handle> on each group heading),
 *   on load and on hashchange, opens the question when the target is one, and scrolls to it (the browser's
 *   own scroll finds nothing while the group is hidden). `--zg-faq-bar-offset` (the tab row's height and
 *   bottom margin, kept current) lets the heading's scroll margin leave the tab row in view. A question with
 *   an Anchor ID is such a target too (#shipping-and-returns); without tabs, that is all this element does,
 *   apart from the editor's block select;
 * - in the theme editor, opens a Question block when the block is selected, first selecting its category when
 *   there are tabs (the section loads this script in the editor even with a single category).
 */
class ZgFaqTabs extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {ResizeObserver | null} */
  #barObserver = null;

  #selected = -1;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    // Theme editor: registered before the checks below, so a single category (no tabs) gets it too.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    const bar = this.querySelector('[data-zg-faq-tabs-bar]');
    const tablist = bar?.querySelector('[role="tablist"]');
    const tabs = Array.from(this.querySelectorAll('[data-zg-faq-tab]')).filter((el) => el instanceof HTMLElement);
    const panels = Array.from(this.querySelectorAll('[data-zg-faq-panel]')).filter((el) => el instanceof HTMLElement);
    const tabsMatchPanels = tabs.length > 1 && tabs.length === panels.length;
    if (!(bar instanceof HTMLElement) || !(tablist instanceof HTMLElement) || !tabsMatchPanels) {
      // No tabs: every question is visible, but a link to a question's anchor still has to open it.
      const target = this.#hashTarget();
      if (target) {
        this.#openQuestion(target);
        this.#reveal(target, 'instant');
      }
      window.addEventListener('hashchange', this.#onHashChange, { signal });
      return;
    }

    this.#tabs = tabs;
    this.#panels = panels;
    this.#tablist = tablist;

    panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabs[index]?.id ?? '');
    });

    // Already rendered by the section; set here too so the CSS states can't disagree with this script.
    this.toggleAttribute('data-zg-tabbed', true);

    // Measured now (the hash scroll below needs it) and again whenever the row's size changes.
    this.#measureBar(bar);
    this.#barObserver = new ResizeObserver(() => this.#measureBar(bar));
    this.#barObserver.observe(bar);

    const target = this.#hashTarget();
    const initial = target ? this.#panelIndexOf(target) : tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(Math.max(0, initial), { closeQuestions: false });
    if (target) {
      this.#openQuestion(target);
      this.#reveal(target, 'instant');
    }

    tablist.addEventListener('click', this.#onClick, { signal });
    tablist.addEventListener('keydown', this.#onKeydown, { signal });
    // `toggle` doesn't bubble; capture it from every question in this element.
    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    window.addEventListener('hashchange', this.#onHashChange, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#barObserver?.disconnect();
    this.#barObserver = null;
  }

  /**
   * Stores the tab row's height plus its bottom margin for the group headings' scroll margin.
   * @param {HTMLElement} bar
   */
  #measureBar(bar) {
    const offset = bar.offsetHeight + (parseFloat(getComputedStyle(bar).marginBlockEnd) || 0);
    this.style.setProperty('--zg-faq-bar-offset', `${Math.ceil(offset)}px`);
  }

  /**
   * Scrolls the page to the element (its scroll margin keeps the header and tab row clear of it).
   * @param {HTMLElement} element
   * @param {ScrollBehavior} behavior - Used unless the shopper prefers reduced motion.
   */
  #reveal(element, behavior) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'instant' : behavior });
  }

  /**
   * Shows one category.
   * @param {number} index - Index of the tab and panel.
   * @param {{ focus?: boolean, closeQuestions?: boolean }} [options]
   */
  #select(index, { focus = false, closeQuestions = true } = {}) {
    const tab = this.#tabs[index];
    if (!tab) return;
    const changed = index !== this.#selected;

    this.#tabs.forEach((item, i) => {
      const selected = i === index;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });

    this.#panels.forEach((panel, i) => {
      const selected = i === index;
      if (!selected && closeQuestions && changed) {
        for (const details of panel.querySelectorAll('details[open]')) {
          if (details instanceof HTMLDetailsElement) details.open = false;
        }
      }
      panel.hidden = !selected;
    });

    this.#selected = index;
    if (focus) tab.focus();
    this.#scrollTabIntoView(tab);
  }

  /**
   * Scrolls the tab row sideways (only) so the tab is fully visible; the page doesn't move.
   * @param {HTMLElement} tab
   */
  #scrollTabIntoView(tab) {
    const tablist = this.#tablist;
    if (!tablist || tablist.scrollWidth <= tablist.clientWidth) return;
    const listBox = tablist.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const inset = 8;
    let delta = 0;
    if (tabBox.left < listBox.left + inset) delta = tabBox.left - listBox.left - inset;
    else if (tabBox.right > listBox.right - inset) delta = tabBox.right - listBox.right + inset;
    if (delta !== 0) tablist.scrollLeft += delta;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-zg-faq-tab]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const index = this.#tabs.indexOf(tab);
    if (index !== -1) this.#select(index);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const current = this.#tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const last = this.#tabs.length - 1;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = rtl ? current - 1 : current + 1;
        break;
      case 'ArrowLeft':
        next = rtl ? current + 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    if (next < 0) next = last;
    if (next > last) next = 0;

    event.preventDefault();
    this.#select(next, { focus: true });
  };

  /** @param {Event} event */
  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    for (const other of this.querySelectorAll('details[open]')) {
      if (other !== details && other instanceof HTMLDetailsElement) other.open = false;
    }
  };

  // The browser scrolls before this runs, so a target in a hidden group isn't reached: scroll once it's shown.
  #onHashChange = () => {
    const target = this.#hashTarget();
    if (!target) return;
    this.#select(this.#panelIndexOf(target));
    this.#openQuestion(target);
    this.#reveal(target, 'smooth');
  };

  /**
   * Opens the selected Question block; with tabs, shows its category first. Without tabs there are no panels.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const block = event.target;
    if (!(block instanceof HTMLElement) || !this.contains(block)) return;
    const index = this.#panelIndexOf(block);
    if (index !== -1) this.#select(index);
    this.#openQuestion(block);
  };

  /**
   * The element inside this one that the URL fragment points to.
   * @returns {HTMLElement | null}
   */
  #hashTarget() {
    let id = '';
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return null;
    }
    if (!id) return null;
    const target = document.getElementById(id);
    return target instanceof HTMLElement && this.contains(target) ? target : null;
  }

  /**
   * @param {Element} element
   * @returns {number} Index of the panel that holds the element, or -1.
   */
  #panelIndexOf(element) {
    return this.#panels.findIndex((panel) => panel.contains(element));
  }

  /**
   * Opens the question that is, or holds, the element.
   * @param {Element} element
   */
  #openQuestion(element) {
    const details = element.closest('details');
    if (details instanceof HTMLDetailsElement && this.contains(details)) details.open = true;
  }
}

if (!customElements.get('zg-faq-tabs')) {
  customElements.define('zg-faq-tabs', ZgFaqTabs);
}
